import React, { FC, PropsWithChildren, useState } from 'react'

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
} from '@floating-ui/react'

import { Badge, BadgeColors, BadgeProps } from '../Badge'
import * as S from './Badges.styles'

interface BadgesHiddenProps {
  hiddenBadges: BadgeProps[]
}

export const BadgesHidden: FC<PropsWithChildren<BadgesHiddenProps>> = ({ hiddenBadges }) => {
  const [visibleHiddenBadges, setVisibleHiddenBadges] = useState(false)

  const { context, refs, floatingStyles } = useFloating<HTMLElement>({
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    strategy: 'absolute',
    onOpenChange: setVisibleHiddenBadges,
    open: visibleHiddenBadges,
    middleware: [offset(2), flip(), shift({ padding: 8 })],
  })

  const hover = useHover(context, {
    delay: { open: 0, close: 100 },
    handleClose: safePolygon(),
  })

  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss])

  return (
    <>
      <div ref={refs.setReference} role="button" tabIndex={0}>
        <Badge badgeColor={BadgeColors.neutralMedium} interactive {...getReferenceProps()}>
          {`+${hiddenBadges.length}`}
        </Badge>
      </div>

      {visibleHiddenBadges ? (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <S.HiddenBadgesContainer
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {hiddenBadges.map((badge, index) => (
                <Badge key={`${badge.dataCy}-${index}`} {...badge} />
              ))}
            </S.HiddenBadgesContainer>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  )
}
